import { createFeatureSelector, createSelector } from "@ngrx/store";
import { Contact, DisplayedInventoryTransactionActivity, InventoryTransaction, InventoryTransactionType, MetalsHardwareItem, MetalsHardwareItemType, MetalsLaserMaterial, MetalsProduct, MetalsProductSetup, MetalsProductVersion, MetalsProductVersionStatus, MetalsVendorHardwareData, StampingStation, StampingTask, Tag, ToolRoomMachine, toEntityDict } from "@prism/prism-shared";
import { MetalsState } from "./metals.reducer";

export const metalsState = createFeatureSelector<MetalsState>('metals');

export const getLoadingState = createSelector(
    metalsState,
    (state: MetalsState): number => state.loadingState
);

export const getError = createSelector(
    metalsState,
    (state: MetalsState): any => state.error
);

export const getHardwareItems = createSelector(
    metalsState,
    (state: MetalsState): MetalsHardwareItem[] => {
        return state.hardwareItems
    }
);

export const getVendorHardwareData = createSelector(
    metalsState,
    (state: MetalsState): MetalsVendorHardwareData[] => state.vendorHardwareData
);

export const getContacts = createSelector(
    metalsState,
    (state: MetalsState): Contact[] => state.contacts
);

export const getTags = createSelector(
    metalsState,
    (state: MetalsState): Tag[] => state.tags
);

export const getHardwareInventoryTransactions = createSelector(
    metalsState,
    (state: MetalsState): InventoryTransaction[] => state.inventoryTransactions.filter(t => t.type === InventoryTransactionType.MetalsHardware)
);


export const getProducts = createSelector(
    metalsState,
    (state: MetalsState): MetalsProduct[] => state.products
);

export const getProductVersions = createSelector(
    metalsState,
    (state: MetalsState): MetalsProductVersion[] => state.productVersions
);

export const getStampingTasks = createSelector(
    metalsState,
    (state: MetalsState): StampingTask[] => state.stampingTasks
);

export const getStampingStations = createSelector(
    metalsState,
    (state: MetalsState): StampingStation[] => state.stampingStations
);

export const getLaserMaterials = createSelector(
    metalsState,
    (state: MetalsState): MetalsLaserMaterial[] => state.metalsLaserMaterials
);


// ====================== substate base selectors ======================





// above are selectors that ONLY use metalsState
// =========================================================
// below are selectors that are built with selectors above





// =========================================================
// Base Parameterized Selectors
// =========================================================

export function getHardwareItemById(hardwareItemId: string) {
    return createSelector(
        getHardwareItems,
        (hardwareItems: MetalsHardwareItem[]): MetalsHardwareItem => {
            return hardwareItems.find(hardwareItem => hardwareItem.id === hardwareItemId) as MetalsHardwareItem;
        }
    )
}

export function getHardwareItemByType(type: MetalsHardwareItemType) {
    return createSelector(
        getHardwareItems,
        (hardwareItems: MetalsHardwareItem[]): MetalsHardwareItem[] => {
            return hardwareItems.filter(hardwareItem => hardwareItem.type === type)
        }
    )
}

export function getVendorHardwareDataByHardwareItemId(hardwareItemId: string) {
    return createSelector(
        getVendorHardwareData,
        (vendorHardwareData: MetalsVendorHardwareData[]): MetalsVendorHardwareData[] => {
            return vendorHardwareData.filter(vendorHardwareData => vendorHardwareData.parentHardwareItemId === hardwareItemId);
        }
    )
}


// =========================================================
// Base Dictionary Selectors
// =========================================================

export const getContactDict = createSelector(
    getContacts,
    (contacts: Contact[]): Record<string, Contact> => toEntityDict<Contact>(contacts)
);

export const getVendorHardwareDataDict = createSelector(
    getVendorHardwareData,
    (vendorHardwareData: MetalsVendorHardwareData[]): Record<string, MetalsVendorHardwareData> => toEntityDict<MetalsVendorHardwareData>(vendorHardwareData)
);

export const getHardwareItemDict = createSelector(
    getHardwareItems,
    (hardwareItems: MetalsHardwareItem[]): Record<string, MetalsHardwareItem> => toEntityDict<MetalsHardwareItem>(hardwareItems)
);


export const getTagsDict = createSelector(
    getTags,
    (tags: Tag[]): Record<string, Tag> => toEntityDict<Tag>(tags)
);

export const getProductDict = createSelector(
    getProducts,
    (products: MetalsProduct[]): Record<string, MetalsProduct> => toEntityDict<MetalsProduct>(products)
);
export const getProductVersionProductIdDict = createSelector(
    getProductVersions,
    (productVersions: MetalsProductVersion[]): Record<string, MetalsProductVersion> => toEntityDict(productVersions, 'productId')
)

export const getStampingTaskDict = createSelector(
    getStampingTasks,
    (stampingTasks: StampingTask[]): Record<string, StampingTask> => toEntityDict<StampingTask>(stampingTasks)
)



// =========================================================
// Other Base Selectors
// =========================================================

export const getReleasedProductVersions = createSelector(
    getProductVersions,
    (productVersions: MetalsProductVersion[]): MetalsProductVersion[] => {
        return productVersions.filter(productVersion => productVersion.status === MetalsProductVersionStatus.Released)
    }
);

export const getDraftProductVersions = createSelector(
    getProductVersions,
    (productVersions: MetalsProductVersion[]): MetalsProductVersion[] => {
        return productVersions.filter(productVersion => productVersion.status === MetalsProductVersionStatus.Draft)
    }
)

export const getProductIdToReleasedProductVersionDict = createSelector(
    getReleasedProductVersions,
    (versions: MetalsProductVersion[]): Record<string, MetalsProductVersion> => toEntityDict<MetalsProductVersion>(versions, 'productId')
)


export const getProductSetups = createSelector(
    metalsState,
    (state: MetalsState): MetalsProductSetup[] => state.productSetups
);

export const getProductSetupById = (productSetupId: string) => createSelector(
    getProductSetups,
    (productSetups: MetalsProductSetup[]): MetalsProductSetup | undefined =>
        productSetups.find((setup) => setup.id === productSetupId)
);

export const getToolRoomMachines = createSelector(
    metalsState,
    (state: MetalsState): ToolRoomMachine[] =>
        [...state.toolRoomMachines].sort((a, b) =>
            a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
        ),
);

/** All machines, alphabetical (previously filtered by active flag — active was removed). */
export const getActiveToolRoomMachines = createSelector(
    getToolRoomMachines,
    (machines: ToolRoomMachine[]): ToolRoomMachine[] => machines,
);

export const getToolRoomMachinesDict = createSelector(
    getToolRoomMachines,
    (machines: ToolRoomMachine[]): Record<string, ToolRoomMachine> => toEntityDict(machines)
);

export const getRecentHardwareInventoryActivity = createSelector(
    getHardwareInventoryTransactions,
    (inventoryTransactions: InventoryTransaction[]): Record<string, DisplayedInventoryTransactionActivity> => {
        const _24HoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000)

        const filteredTransactions = inventoryTransactions.filter(transaction => transaction.createdOn.getTime() > _24HoursAgo.getTime())
        const uniqueHardwareIds = [...new Set(filteredTransactions.map(transaction => transaction.entityId))]

        const activity: Record<string, DisplayedInventoryTransactionActivity> = {}

        uniqueHardwareIds.forEach(hardwareItemId => {

            const transactionsForHardwareItem = filteredTransactions.filter(transaction => transaction.entityId === hardwareItemId)

            const net = transactionsForHardwareItem.reduce((acc, transaction) => (acc + transaction.change), 0)
            const increase = transactionsForHardwareItem.filter(transaction => transaction.change > 0).reduce((acc, transaction) => (acc + transaction.change), 0)
            const decrease = transactionsForHardwareItem.filter(transaction => transaction.change < 0).reduce((acc, transaction) => (acc + transaction.change), 0)

            activity[hardwareItemId] = {
                net,
                increase,
                decrease
            }
        })

        return activity
    }

)


